// Filtro allergeni e diete del POS (YELLGO-6213).
// Logica pura, senza React: stato dei prodotti in griglia, sostituzione automatica dei sottoprodotti,
// scambio nelle categorie a scelta singola e composizione delle note di riga per la comanda.
//
// filter = { allergens: [id, ...], diets: ["vegan" | "gluten_free" | "lactose_free", ...] }
// Solo i sottoprodotti (products / products2) hanno allergeni e diete: le varianti semplici non sono mai "a rischio".

export const ALLERGEN_GLUTEN = 1;
export const ALLERGEN_MILK = 7;

export const DIETS = [
    { id: "vegan", label: "Vegano" },
    { id: "gluten_free", label: "Senza glutine" },
    { id: "lactose_free", label: "Senza lattosio" },
];

// nomi per la comanda (la cucina legge in italiano), id dei 14 allergeni UE di api/config/allergens.json
export const ALLERGEN_PRINT_NAMES = {
    1: "Glutine",
    2: "Crostacei",
    3: "Uova",
    4: "Pesce",
    5: "Arachidi",
    6: "Soia",
    7: "Latte",
    8: "Frutta a guscio",
    9: "Sedano",
    10: "Senape",
    11: "Sesamo",
    12: "Solfiti",
    13: "Lupini",
    14: "Molluschi",
};

export const NOTE_ALLERGY_PREFIX = "ALLERGIA: ";
export const NOTE_DIET_PREFIX = "DIETA: ";

const clone = (o) => (typeof structuredClone === "function" ? structuredClone(o) : JSON.parse(JSON.stringify(o)));

const VARIANT_GROUPS = ["variants", "variants2", "products", "products2"];
const SUBPRODUCT_GROUPS = ["products", "products2"];

export function emptyFilter() {
    return { allergens: [], diets: [] };
}

export function isFilterActive(filter) {
    return !!filter && (filter.allergens?.length || 0) + (filter.diets?.length || 0) > 0;
}

export function filterKey(filter) {
    if (!isFilterActive(filter)) return "";
    const allergens = [...filter.allergens].map(Number).sort((a, b) => a - b);
    const diets = [...filter.diets].sort();
    return allergens.join(",") + "|" + diets.join(",");
}

export function isIngredientsCategory(vc) {
    return Number(vc?.ingredients) === 1 || Number(vc?.is_ingredients) === 1 || Number(vc?.ingredienti) === 1;
}

function allergenIds(item) {
    return (item?.allergens || []).map((a) => Number(a && typeof a === "object" ? a.id : a));
}

function qtyOf(item) {
    return Number(item?.qty || 0);
}

// Motivi per cui un prodotto o sottoprodotto non va bene per il filtro.
// - allergene escluso presente
// - vegano: il prodotto deve essere marcato vegano (nessun allergene lo indica)
// - senza glutine: contiene l'allergene glutine
// - senza lattosio: contiene latte e non è marcato senza lattosio (la mozzarella senza lattosio contiene latte)
export function riskReasons(item, filter) {
    if (!isFilterActive(filter) || !item) return [];
    const ids = allergenIds(item);
    const reasons = [];
    for (const id of filter.allergens.map(Number)) {
        if (ids.includes(id)) reasons.push({ type: "allergen", id });
    }
    const diets = filter.diets || [];
    if (diets.includes("vegan") && Number(item.vegan) !== 1) reasons.push({ type: "diet", id: "vegan" });
    if (diets.includes("gluten_free") && ids.includes(ALLERGEN_GLUTEN)) reasons.push({ type: "diet", id: "gluten_free" });
    if (diets.includes("lactose_free") && ids.includes(ALLERGEN_MILK) && Number(item.lactose_free) !== 1)
        reasons.push({ type: "diet", id: "lactose_free" });
    return reasons;
}

export function isRisky(item, filter) {
    return riskReasons(item, filter).length > 0;
}

export function reasonLabel(reason) {
    if (reason.type === "allergen") return ALLERGEN_PRINT_NAMES[reason.id] || "Allergene " + reason.id;
    if (reason.id === "vegan") return "non vegano";
    if (reason.id === "gluten_free") return "glutine";
    if (reason.id === "lactose_free") return "lattosio";
    return String(reason.id);
}

export function reasonsLabel(reasons) {
    return [...new Set(reasons.map(reasonLabel))].join(", ");
}

export function countSelected(vc) {
    let tot = 0;
    for (const group of VARIANT_GROUPS) for (const item of vc?.[group] || []) tot += qtyOf(item);
    return tot;
}

function getMaxValue(vc) {
    const max = Number(vc?.max);
    return Number.isFinite(max) ? max : Number.MAX_SAFE_INTEGER;
}

// Selezione predefinita come VariantsPopUp.setDefaults: ogni voce con is_default > 0 parte a 1.
export function applyDefaults(product) {
    for (const vc of product?.variants_categories || []) {
        for (const group of VARIANT_GROUPS) {
            for (const item of vc[group] || []) item.qty = Number(item.is_default) > 0 ? 1 : 0;
        }
        vc.selected = countSelected(vc);
    }
    return product;
}

// Sostituisce i sottoprodotti selezionati a rischio con la prima alternativa compatibile della stessa categoria
// (ordine delle voci = posizione). Modifica il prodotto passato.
// Senza alternativa la voce è "unresolved" e il prodotto non è selezionabile, anche se la voce sarebbe removable.
// Le categorie "ingredienti" non sostituiscono (lì tutte le voci sono predefinite): la voce removibile viene tolta
// ("remove"), sempre che la categoria non scenda sotto il minimo; altrimenti è "unresolved".
// opts.manual: non sceglie l'alternativa, toglie la voce e lascia scegliere l'operatore.
// opts.freeSubstitute: l'alternativa non aggiunge il suo sovrapprezzo.
export function autoSubstitute(product, filter, opts = {}) {
    const changes = [];
    if (!isFilterActive(filter)) return changes;
    (product?.variants_categories || []).forEach((vc, vcIndex) => {
        for (const group of SUBPRODUCT_GROUPS) {
            (vc[group] || []).forEach((item, index) => {
                if (qtyOf(item) <= 0 || !isRisky(item, filter)) return;
                const from = { vcIndex, group, index, name: item.name, id: item.id };
                if (isIngredientsCategory(vc)) {
                    const min = Number(vc.min);
                    const keepsMin = !Number.isFinite(min) || countSelected(vc) - qtyOf(item) >= min;
                    if (Number(item.removable) === 0 || !keepsMin) {
                        changes.push({ type: "unresolved", vcName: vc.name, from });
                        return;
                    }
                    item.qty = 0;
                    item.allergen_removed = 1;
                    changes.push({ type: "remove", vcName: vc.name, from });
                    return;
                }
                let alt = null;
                for (const altGroup of SUBPRODUCT_GROUPS) {
                    (vc[altGroup] || []).forEach((candidate, altIndex) => {
                        if (alt || candidate === item || qtyOf(candidate) > 0 || isRisky(candidate, filter)) return;
                        alt = { group: altGroup, index: altIndex, item: candidate };
                    });
                }
                if (!alt) {
                    changes.push({ type: "unresolved", vcName: vc.name, from });
                    return;
                }
                if (opts.manual) {
                    item.qty = 0;
                    changes.push({ type: "manual", vcName: vc.name, from });
                    return;
                }
                alt.item.qty = qtyOf(item);
                item.qty = 0;
                alt.item.allergen_substitute = 1;
                if (opts.freeSubstitute) {
                    alt.item.allergen_original_price = alt.item.price;
                    alt.item.price = 0;
                }
                changes.push({
                    type: "swap",
                    vcName: vc.name,
                    from,
                    to: { vcIndex, group: alt.group, index: alt.index, name: alt.item.name, id: alt.item.id },
                });
            });
        }
        vc.selected = countSelected(vc);
    });
    return changes;
}

// Stato in griglia: "ok" | "adaptable" | "locked", con i motivi del blocco.
export function productStatus(product, filter) {
    if (!isFilterActive(filter)) return { status: "ok", reasons: [], changes: [] };
    const own = riskReasons(product, filter);
    if (own.length) return { status: "locked", reasons: own, changes: [] };
    if (!product?.variants_categories?.length) return { status: "ok", reasons: [], changes: [] };
    const simulated = applyDefaults(clone(product));
    const changes = autoSubstitute(simulated, filter);
    const unresolved = changes.filter((c) => c.type === "unresolved");
    if (unresolved.length) {
        const reasons = [];
        for (const c of unresolved) {
            const item = product.variants_categories[c.from.vcIndex][c.from.group][c.from.index];
            reasons.push(...riskReasons(item, filter));
        }
        return { status: "locked", reasons, changes, unresolved };
    }
    return { status: changes.length ? "adaptable" : "ok", reasons: [], changes };
}

// Scambio nelle categorie a scelta singola: con max 1 il "+" su un'altra voce toglie quella selezionata.
// Non vale per le categorie force_mono (ogni voce è un sì/no a sé) né se la voce selezionata è un ingrediente
// non rimovibile. Restituisce true se ha scambiato (modifica la categoria passata).
export function swapInSingleChoice(vc, group, index) {
    if (!vc || Number(vc.force_mono) === 1 || getMaxValue(vc) !== 1) return false;
    const target = vc[group]?.[index];
    if (!target || qtyOf(target) > 0 || countSelected(vc) < 1) return false;
    for (const g of VARIANT_GROUPS) {
        for (const item of vc[g] || []) {
            if (qtyOf(item) > 0 && isIngredientsCategory(vc) && Number(item.removable) === 0) return false;
        }
    }
    for (const g of VARIANT_GROUPS) for (const item of vc[g] || []) item.qty = 0;
    target.qty = 1;
    vc.selected = countSelected(vc);
    return true;
}

export function filterNoteLines(filter) {
    if (!isFilterActive(filter)) return [];
    const lines = [];
    const allergens = [...filter.allergens].map(Number).sort((a, b) => a - b);
    if (allergens.length)
        lines.push(NOTE_ALLERGY_PREFIX + allergens.map((id) => ALLERGEN_PRINT_NAMES[id] || id).join(", ").toUpperCase());
    const diets = DIETS.filter((d) => filter.diets.includes(d.id));
    if (diets.length) lines.push(NOTE_DIET_PREFIX + diets.map((d) => d.label).join(", ").toUpperCase());
    return lines;
}

// "No <nome>" per i sottoprodotti predefiniti portati a 0. Le varianti force_mono a 0 le stampa già l'agente.
export function removedDefaultsNotes(product) {
    const lines = [];
    for (const vc of product?.variants_categories || []) {
        for (const group of SUBPRODUCT_GROUPS) {
            for (const item of vc[group] || []) {
                if (Number(item.is_default) > 0 && qtyOf(item) <= 0) lines.push("No " + item.name);
            }
        }
    }
    return lines;
}

// Dati allergeni di una riga nuova, calcolati quando il prodotto entra nel carrello (add_product):
// allergy_filter (copia del filtro attivo, assente per i prodotti non contaminabili) e removed_defaults.
// Le righe ricaricate da un ordine salvato non li hanno mai: la loro nota è già quella salvata.
export function lineAllergenData(product, filter) {
    const data = { removed_defaults: removedDefaultsNotes(product).map((l) => l.slice(3)) };
    if (isFilterActive(filter) && Number(product?.not_contaminable) !== 1)
        data.allergy_filter = { allergens: [...filter.allergens], diets: [...filter.diets] };
    return data;
}

// Nota di riga inviata all'API: "ALLERGIA/DIETA", "No <predefinito tolto>", ingredienti tolti, note libere.
// Solo le righe create in questa sessione hanno allergy_filter / removed_defaults: le righe già inviate
// (anche se divise o regalate) tengono la nota salvata, così riaprire e reinviare non crea duplicati.
// ingredientsNotes è l'output di prepareIngredients (App.js).
export function composeLineNotes(line, { ingredientsNotes = "", withAllergenInfo = false } = {}) {
    const free = line?.notes ? line.notes : "";
    if (!withAllergenInfo || line?.item_id) return ingredientsNotes + free;
    const head = [...filterNoteLines(line.allergy_filter), ...(line.removed_defaults || []).map((n) => "No " + n)];
    return (head.length ? head.join("\n") + "\n" : "") + ingredientsNotes + free;
}

// Allergia e dieta lette dalla nota di una riga già inviata, per mostrarle nel carrello.
export function parseAllergyFromNotes(notes) {
    const res = { allergy: null, diet: null };
    for (const raw of String(notes || "").split("\n")) {
        const line = raw.trim();
        if (line.startsWith(NOTE_ALLERGY_PREFIX)) res.allergy = line.slice(NOTE_ALLERGY_PREFIX.length);
        if (line.startsWith(NOTE_DIET_PREFIX)) res.diet = line.slice(NOTE_DIET_PREFIX.length);
    }
    return res;
}
